import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartCard from './ChartCard';
import { useThemeColors } from './useThemeColors';

interface BarChartCardProps {
  title: string;
  subtitle?: string;
  data: Array<Record<string, string | number>>;
  dataKey: string;
  categoryKey: string;
  layout?: 'horizontal' | 'vertical';
  height?: number;
  colorByIndex?: boolean;
  /**
   * Marca uma linha de referência no eixo de valor (ex.: 100% de utilização).
   * Sem a prop, o componente renderiza exatamente como antes.
   */
  referenceValue?: number;
  referenceLabel?: string;
  /**
   * Número visível na ponta de cada barra, sem passar o mouse. `key` escolhe o campo mostrado
   * (default: o próprio `dataKey`) — útil quando a barra é saturada e o texto deve dizer o valor
   * real. Sem a prop, nada muda.
   */
  valueLabel?: { key?: string; format: (v: number) => string };
}

const ROTULO_FONTE = 14;

const BarChartCard: FC<BarChartCardProps> = ({
  title,
  subtitle,
  data,
  dataKey,
  categoryKey,
  layout = 'horizontal',
  height,
  colorByIndex = false,
  referenceValue,
  referenceLabel,
  valueLabel,
}) => {
  const colors = useThemeColors();

  if (!data || data.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle} height={height}>
        <div className="w-full h-full flex items-center justify-center text-content-muted text-sm">
          Sem dados no período
        </div>
      </ChartCard>
    );
  }

  const isVertical = layout === 'vertical';

  return (
    <ChartCard title={title} subtitle={subtitle} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout={layout}
          margin={{
            top: valueLabel && !isVertical ? 24 : 8,
            right: valueLabel && isVertical ? 56 : 16,
            bottom: 0,
            left: isVertical ? 24 : 0,
          }}
        >
          <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" />
          {isVertical ? (
            <>
              <XAxis
                type="number"
                stroke={colors.textMuted}
                tick={{ fill: colors.textMuted, fontSize: 13 }}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey={categoryKey}
                stroke={colors.textMuted}
                tick={{ fill: colors.textMuted, fontSize: 13 }}
                width={120}
              />
            </>
          ) : (
            <>
              <XAxis
                type="category"
                dataKey={categoryKey}
                stroke={colors.textMuted}
                tick={{ fill: colors.textMuted, fontSize: 13 }}
                interval={0}
                angle={-15}
                textAnchor="end"
                height={60}
              />
              <YAxis
                type="number"
                stroke={colors.textMuted}
                tick={{ fill: colors.textMuted, fontSize: 13 }}
                allowDecimals={false}
              />
            </>
          )}
          <Tooltip
            contentStyle={{
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              color: colors.text,
              fontSize: 14,
            }}
            labelStyle={{ color: colors.textMuted }}
            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
          />
          {referenceValue != null ? (
            // Em `layout="vertical"` o eixo de VALOR é o x — trocar por `y` marca a
            // categoria errada.
            <ReferenceLine
              {...(isVertical ? { x: referenceValue } : { y: referenceValue })}
              // Sem `extendDomain`, o Recharts descarta a referência quando ela cai
              // fora do domínio dos dados — uma meta de 100% sumiria justo no caso em
              // que ninguém a atingiu, que é quando ela mais informa.
              ifOverflow="extendDomain"
              stroke={colors.danger}
              strokeDasharray="4 4"
              label={{ value: referenceLabel, fill: colors.textMuted, fontSize: 13 }}
            />
          ) : null}
          {/* Com número na barra, sem animação: o recharts só desenha o rótulo quando a animação acaba. */}
          <Bar dataKey={dataKey} fill={colors.accent} radius={[4, 4, 0, 0]} isAnimationActive={!valueLabel}>
            {colorByIndex
              ? data.map((_, idx) => (
                  <Cell key={idx} fill={colors.palette[idx % colors.palette.length]} />
                ))
              : null}
            {valueLabel ? (
              <LabelList
                dataKey={valueLabel.key ?? dataKey}
                position={isVertical ? 'right' : 'top'}
                formatter={(v: unknown) => (typeof v === 'number' ? valueLabel.format(v) : '')}
                style={{ fill: colors.text, fontSize: ROTULO_FONTE, fontWeight: 600 }}
              />
            ) : null}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export default BarChartCard;
